import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, CarFront, Gavel, Trophy, Calculator } from "lucide-react";
import { useLang } from "@/i18n";
import api, { fmtUSD, fmtDate, statusLabel, STATUS_COLORS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

function Estimator({ settings }) {
  const { t } = useLang();
  const [price, setPrice] = useState(5000);
  const calc = useMemo(() => {
    const p = Number(price) || 0;
    const fee = settings?.flat_fee ?? 1000;
    const taxes = Math.round(p * 0.06 * 100) / 100;
    const transport = 800;
    return { fee, taxes, transport, total: p + fee + taxes + transport };
  }, [price, settings]);

  return (
    <div className="card-tech tracing-beam rounded-xl p-6" data-testid="cost-estimator">
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="w-5 h-5 text-[#2563EB]" strokeWidth={1.5} />
        <h3 className="text-lg font-semibold">{t("Estimador de costos")}</h3>
      </div>
      <Label className="text-slate-400 text-xs uppercase tracking-widest">{t("Precio estimado del vehículo (USD)")}</Label>
      <Input data-testid="estimator-price" type="number" min="0" value={price}
        onChange={(e) => setPrice(e.target.value)}
        className="mt-2 bg-[#0B0F1A] border-white/15 focus:ring-2 focus:ring-blue-500" />
      <div className="mt-5 space-y-2.5 text-sm">
        <div className="flex justify-between text-slate-300"><span>{t("Vehículo")}</span><span data-testid="estimator-vehicle">{fmtUSD(Number(price) || 0)}</span></div>
        <div className="flex justify-between text-slate-300"><span>{t("Comisión de bróker")}</span><span data-testid="estimator-fee">{fmtUSD(calc.fee)}</span></div>
        <div className="flex justify-between text-slate-300"><span>{t("Impuestos estimados (6%)")}</span><span data-testid="estimator-taxes">{fmtUSD(calc.taxes)}</span></div>
        <div className="flex justify-between text-slate-300"><span>{t("Transporte estimado")}</span><span data-testid="estimator-transport">{fmtUSD(calc.transport)}</span></div>
        <div className="border-t border-white/10 pt-3 flex justify-between font-bold text-white">
          <span>{t("Total estimado")}</span><span data-testid="estimator-total" className="text-[#22C55E]">{fmtUSD(calc.total)}</span>
        </div>
        <p className="text-[11px] text-slate-500 pt-1">{t("Estimación referencial. No incluye tarifas del patio de Copart ni reparaciones.")}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const [bids, setBids] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/bid-requests"), api.get("/settings")])
      .then(([b, s]) => { setBids(b.data); setSettings(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const active = bids.filter((b) => !["lost", "delivered", "refunded", "cancelled"].includes(b.status)).length;
  const won = bids.filter((b) => ["won", "payment_due", "paid", "in_transit", "delivered"].includes(b.status)).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{t("Mi panel")}</h1>
          <p className="text-slate-400 text-sm mt-1">{t("Rastrea tus solicitudes de puja en Copart.")}</p>
        </div>
        <Link to="/panel/nueva">
          <Button data-testid="new-bid-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-6">
            <PlusCircle className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("Nueva solicitud")}
          </Button>
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: CarFront, label: t("Solicitudes"), value: bids.length, testid: "stat-total" },
              { icon: Gavel, label: t("Activas"), value: active, testid: "stat-active" },
              { icon: Trophy, label: t("Ganadas"), value: won, testid: "stat-won" },
            ].map((s) => (
              <div key={s.label} className="card-tech rounded-xl p-5">
                <s.icon className="w-5 h-5 text-[#2563EB] mb-3" strokeWidth={1.5} />
                <p className="font-display text-2xl font-extrabold" data-testid={s.testid}>{s.value}</p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>

          {loading ? (
            <div className="card-tech rounded-xl p-10 text-center text-slate-400 text-sm">{t("Cargando solicitudes...")}</div>
          ) : bids.length === 0 ? (
            <div className="card-tech rounded-xl p-12 text-center" data-testid="empty-bids">
              <CarFront className="w-10 h-10 text-slate-600 mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-slate-300 font-semibold mb-1">{t("Aún no tienes solicitudes")}</p>
              <p className="text-sm text-slate-500 mb-6">{t("Envía tu primer lote de Copart y autoriza tu puja máxima.")}</p>
              <Link to="/panel/nueva"><Button data-testid="empty-new-bid-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full">{t("Enviar un lote")}</Button></Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="bid-list">
              {bids.map((b, i) => {
                const lot = b.lot || {};
                const vehicle = `${lot.year || ""} ${lot.make || ""} ${lot.model || ""}`.trim() || `Lote ${lot.lot_number || "—"}`;
                return (
                  <Link key={b.id} to={`/panel/solicitud/${b.id}`} data-testid={`bid-card-${b.id}`}>
                    <div className="card-tech rounded-xl p-6 fade-up flex flex-wrap items-center gap-4 justify-between" style={{ animationDelay: `${i * 60}ms` }}>
                      <div className="flex items-center gap-4 min-w-0">
                        {lot.image_url ? (
                          <img src={lot.image_url} alt={vehicle} className="w-20 h-14 object-cover rounded-lg border border-white/10" />
                        ) : (
                          <div className="w-20 h-14 rounded-lg bg-[#0B0F1A] border border-white/10 flex items-center justify-center">
                            <CarFront className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-white truncate">{vehicle}</p>
                          <p className="text-xs text-slate-500">Lote #{lot.lot_number || "—"} · {fmtDate(b.created_at)}</p>
                          <p className="text-xs text-slate-400 mt-1">{t("Puja máx:")} <span className="text-slate-200 font-semibold">{fmtUSD(b.max_bid)}</span></p>
                        </div>
                      </div>
                      <Badge className={`${STATUS_COLORS[b.status]} border text-xs px-3 py-1`} data-testid={`bid-status-${b.id}`}>
                        {statusLabel(b.status, lang)}
                      </Badge>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        <Estimator settings={settings} />
      </div>
    </div>
  );
}
